import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, Check, ChevronDown, History, ImageOff, Images } from 'lucide-react';
import {
  getBlockchainExplorerTxUrl,
  getBlockchainExplorerUrl,
  getImageVerification,
  getPublicProduct,
  ImageVerification,
  ImageVerificationImage,
  PublicProduct,
} from '../../../services/publicProductService';
import VaultSheet from './VaultSheet';
import '../../../styles/shopper.css';

// The record's history, when the page can show earlier versions.
export interface VerifiedVersions {
  // How many times the brand updated the record since creating it.
  updates: number;
  viewingOlder: boolean;
  // "Version 2 of 3", shown while an earlier version is on screen.
  position?: string;
  canGoOlder: boolean;
  onOlder: () => void;
  onLatest: () => void;
}

interface VerifiedSheetProps {
  // The sheet is open while this is set.
  productId: string | null;
  brandName?: string;
  onClose: () => void;
  // The product page already has these. Passing them skips a second request
  // for the product, which would count as another scan when signed out.
  product?: PublicProduct | null;
  images?: ImageVerification | null;
  versions?: VerifiedVersions | null;
}

const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const times = (n: number) => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`);

const PHOTO_STATUS: Record<ImageVerificationImage['status'], string> = {
  verified: 'Matches',
  mismatch: 'Changed',
  unanchored: 'Not registered',
  'missing-in-s3': 'Missing',
};

const shortId = (id: string) => (id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id);

// The record object behind the label: an item's own, else its product's.
export const recordIdOf = (product: PublicProduct | null | undefined): string | undefined =>
  product ? (product.type === 'sub' && product.objectId ? product.objectId : product.mainObjectId) || undefined : undefined;

// What "Verified" means for one piece, in plain words (#283: no jargon, no
// overclaiming): who created the record behind the label and when, whether
// the photos still match, how often it changed, and the public record with
// its technical details for the curious. Shared by the vault and the
// product page.
const VerifiedSheet: React.FC<VerifiedSheetProps> = ({
  productId,
  brandName,
  onClose,
  product: givenProduct,
  images: givenImages,
  versions,
}) => {
  const [fetchedProduct, setFetchedProduct] = useState<PublicProduct | null>(null);
  const [fetchedImages, setFetchedImages] = useState<ImageVerification | null>(null);
  const [failed, setFailed] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasProduct = givenProduct !== undefined;
  const hasImages = givenImages !== undefined;

  useEffect(() => {
    setDetailsOpen(false);
    if (!productId) return;
    let cancelled = false;
    setFailed(false);
    if (!hasProduct) {
      setFetchedProduct(null);
      // A saved piece re-opened by its saver doesn't count as a brand scan.
      getPublicProduct(productId)
        .then(p => !cancelled && setFetchedProduct(p))
        .catch(() => !cancelled && setFailed(true));
    }
    if (!hasImages) {
      setFetchedImages(null);
      getImageVerification(productId).then(v => !cancelled && setFetchedImages(v));
    }
    return () => {
      cancelled = true;
    };
  }, [productId, hasProduct, hasImages]);

  const product = hasProduct ? givenProduct : fetchedProduct;
  const images = hasImages ? givenImages : fetchedImages;
  const brand = product?.creator?.displayName || brandName || 'the brand';
  // Brand names are self-declared: only an account BasiQR checked is named as the
  // brand (pentest 2026-10-30, C1). Otherwise the record is the account's.
  const brandVerified = product?.creator?.verified === true;
  const owner = brandVerified ? brand : 'this account';
  const Owner = brandVerified ? brand : 'This account';
  const recordId = recordIdOf(product);
  const photos = images && images.overall !== 'unanchored' ? images : null;
  const showVersions = !!versions && (versions.updates > 0 || versions.viewingOlder);
  const hasDetails = !!recordId || !!photos;

  const older = () => {
    versions?.onOlder();
    onClose();
  };
  const latest = () => {
    versions?.onLatest();
    onClose();
  };

  // The seal (#467): the product page's woven ticket, amber while an earlier
  // version is on screen or a photo changed.
  const seal = versions?.viewingOlder
    ? { state: 'history', title: 'Earlier version', sub: versions.position ?? 'Not the latest' }
    : photos?.overall === 'mismatch'
      ? { state: 'changed', title: 'Photo changed', sub: 'since it was registered' }
      : { state: 'verified', title: 'Verified', sub: 'Checked on scan' };

  return (
    <VaultSheet open={!!productId} onClose={onClose} maxWidth="sm:max-w-sm" label="Verified" tall>
      <div className="flex flex-col items-center pt-3 text-center">
        <span className="pp-ticket vs-ticket" data-trust={seal.state} aria-hidden="true">
          {seal.state === 'verified' ? <Check size={17} /> : seal.state === 'history' ? <History size={17} /> : <ImageOff size={17} />}
          <span>
            <b>{seal.title}</b>
            <small>{seal.sub}</small>
          </span>
        </span>
        <h3 className="sr-only">Verified</h3>
        <p className="mt-5 max-w-[32ch] text-[15px] leading-snug text-[var(--v-text-2)]">
          {product
            ? brandVerified
              ? `${brand} created the record behind this label on ${longDate(product.createdAt)}. Only ${brand} can update it, and every update stays visible.`
              : `An account named “${brand}” created the record behind this label on ${longDate(product.createdAt)}. BasiQR has not checked that this account is ${brand}. Only this account can update the record, and every update stays visible.`
            : failed
              ? 'The record behind this label could not be loaded right now.'
              : 'Loading the record behind this label…'}
        </p>
      </div>

      {(product || photos || showVersions) && (
        <dl className="vs-rows mt-5">
          {product && (
            <div>
              <dt>Registered</dt>
              <dd>{longDate(product.createdAt)}</dd>
            </div>
          )}
          {photos && (
            <div>
              <dt>Photos</dt>
              <dd className={photos.overall === 'mismatch' ? 'is-warn' : photos.overall === 'verified' ? 'is-ok' : undefined}>
                {photos.overall === 'mismatch' ? <ImageOff size={15} aria-hidden="true" /> : <Images size={15} aria-hidden="true" />}
                <span>
                  {photos.overall === 'verified'
                    ? `The photos are the ones ${owner} registered.`
                    : photos.overall === 'partial'
                      ? `Some photos are the ones ${owner} registered; others were added later.`
                      : `Some photos changed since ${owner} registered them.`}
                </span>
              </dd>
            </div>
          )}
          {showVersions && versions && (
            <div>
              <dt>Versions</dt>
              <dd className={versions.viewingOlder ? 'is-warn' : undefined}>
                <History size={15} aria-hidden="true" />
                <span>
                  {versions.viewingOlder
                    ? `You're looking at an earlier version${versions.position ? ` (${versions.position.toLowerCase()})` : ''}.`
                    : `${Owner} updated this record ${times(versions.updates)}. Every version stays visible.`}
                </span>
              </dd>
              <span className="vs-actions">
                {versions.viewingOlder && (
                  <button type="button" onClick={latest} className="v-btn v-btn-primary !h-9 !rounded-full !px-4 text-[13.5px]">
                    Back to the latest
                  </button>
                )}
                {versions.canGoOlder && (
                  <button type="button" onClick={older} className="v-btn v-btn-ghost !h-9 !rounded-full !px-4 text-[13.5px]">
                    {versions.viewingOlder ? 'Earlier still' : 'See the earlier version'}
                  </button>
                )}
              </span>
            </div>
          )}
        </dl>
      )}

      {recordId && (
        <a href={getBlockchainExplorerUrl(recordId)} target="_blank" rel="noopener noreferrer" className="v-btn v-btn-ghost mt-5 w-full !rounded-full">
          See the public record
          <ArrowUpRight size={16} />
        </a>
      )}

      {hasDetails && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setDetailsOpen(o => !o)}
            aria-expanded={detailsOpen}
            aria-controls="verified-details"
            className="mx-auto flex items-center gap-1 rounded-full px-3 py-1.5 text-[13px] font-medium text-[var(--v-text-3)]"
          >
            Technical details
            <ChevronDown size={15} className={`transition-transform ${detailsOpen ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {detailsOpen && (
              <motion.div
                id="verified-details"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <dl className="mt-2 space-y-2 rounded-2xl p-3.5 text-[13px]" style={{ background: 'var(--v-glass)' }}>
                  {recordId && (
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-[var(--v-text-3)]">Record</dt>
                      <dd className="font-mono text-[12px] text-[var(--v-text-2)]" title={recordId}>
                        {shortId(recordId)}
                      </dd>
                    </div>
                  )}
                  {photos?.images.map(img => {
                    const hash = img.currentHashHex ?? img.anchoredHashHex;
                    return (
                      <div key={`${img.slot}-${img.s3Key}`} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <dt className="text-[var(--v-text-3)]">Photo {img.slot + 1}</dt>
                        <dd className="flex flex-1 flex-wrap items-center justify-end gap-x-3 gap-y-1">
                          <span className={img.status === 'verified' ? 'text-[var(--v-success)]' : img.status === 'unanchored' ? 'text-[var(--v-text-3)]' : 'text-[var(--v-warn)]'}>
                            {PHOTO_STATUS[img.status]}
                          </span>
                          {hash && <code className="font-mono text-[12px] text-[var(--v-text-3)]">{hash.slice(0, 10)}…</code>}
                          {img.anchorTxDigest && (
                            <a
                              href={getBlockchainExplorerTxUrl(img.anchorTxDigest)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-0.5 font-medium text-[var(--v-accent-ink)]"
                              aria-label={`Public record for photo ${img.slot + 1}`}
                            >
                              Record
                              <ArrowUpRight size={13} />
                            </a>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </VaultSheet>
  );
};

export default VerifiedSheet;
